import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import {
  lettersNumbersAndSpecialsRegex,
  lettersOnlyWithSpaceAndHyphenRegex,
} from 'app/common/utils';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { TextField } from 'app/components/form-fields';
import useClickOutside from 'app/hooks/useClickOutside';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import SetNameIcon from 'assets/images/ill_set_name.svg';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { userInfoApi } from 'services/api/user-info/queries';
import z from 'zod/v3';
import './SetUserNamesDialog.less';

interface Props {
  onDismiss: () => void;
  onSuccess: () => void;
}

const schema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, { message: 'set_user_names_dialog.fields.first_name.is_required' })
    .max(64, { message: 'maximum_length_exceeded' })
    .regex(lettersOnlyWithSpaceAndHyphenRegex),
  lastName: z
    .string()
    .trim()
    .min(1, { message: 'set_user_names_dialog.fields.last_name.is_required' })
    .max(64, { message: 'maximum_length_exceeded' })
    .regex(lettersOnlyWithSpaceAndHyphenRegex),
  companyName: z
    .string()
    .trim()
    .max(100, { message: 'maximum_length_exceeded' })
    .regex(lettersNumbersAndSpecialsRegex)
    .or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

export default function SetUserNamesDialog({ onDismiss, onSuccess }: Props) {
  const { t } = useTranslation();

  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const { data: user } = useGetUserInfoQuery();
  const [updateUser, { isLoading }] = userInfoApi.useUpdateUserInfoMutation();

  useClickOutside(onDismiss);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    values: {
      firstName: user?.first_name ?? '',
      lastName: user?.last_name ?? '',
      companyName: user?.company_name ?? '',
    },
  });

  async function onSubmit({ firstName, lastName, companyName }: FormData) {
    try {
      await updateUser({
        firstName,
        lastName,
        companyName: companyName === '' ? null : companyName,
      });
    } catch (error) {
      addErrorModal();
    }
    onSuccess();
  }

  function handleFieldChange(
    value: string,
    regex: RegExp,
    onChange: (value: string) => void,
  ) {
    const nextValue = value || '';

    const cleanValue = nextValue
      .split('')
      .filter(char => regex.test(char))
      .join('');

    onChange(cleanValue);
  }

  return (
    <Dialog className="modal-center modal-remove-item modal-fulltext">
      <div className="set-user-names-dialog">
        <div className="set-user-names-dialog__header">
          <img
            className="set-user-names-dialog__image"
            src={SetNameIcon}
            alt="Set names"
            width={128}
            height={128}
          />

          <h2 className="set-user-names-dialog__title">
            {t('set_user_names_dialog.title')}
          </h2>

          <p
            className="set-user-names-dialog__subtitle"
            style={{ textAlign: 'center' }}
          >
            {t('set_user_names_dialog.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="set-user-names-dialog__content">
            <Controller
              control={control}
              name="firstName"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('set_user_names_dialog.fields.first_name.label')}
                  placeholder={t(
                    'set_user_names_dialog.fields.first_name.placeholder',
                  )}
                  required
                  disabled={isLoading}
                  error={
                    errors.firstName?.message
                      ? t(errors.firstName?.message, { length: 64 })
                      : undefined
                  }
                  onChange={e =>
                    handleFieldChange(
                      e.value,
                      lettersOnlyWithSpaceAndHyphenRegex,
                      field.onChange,
                    )
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="lastName"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('set_user_names_dialog.fields.last_name.label')}
                  placeholder={t(
                    'set_user_names_dialog.fields.last_name.placeholder',
                  )}
                  required
                  disabled={isLoading}
                  error={
                    errors.lastName?.message
                      ? t(errors.lastName?.message, { length: 64 })
                      : undefined
                  }
                  onChange={e =>
                    handleFieldChange(
                      e.value,
                      lettersOnlyWithSpaceAndHyphenRegex,
                      field.onChange,
                    )
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="companyName"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('set_user_names_dialog.fields.company_name.label')}
                  placeholder={t(
                    'set_user_names_dialog.fields.company_name.placeholder',
                  )}
                  disabled={isLoading}
                  error={
                    errors.companyName?.message
                      ? t(errors.companyName?.message, { length: 100 })
                      : undefined
                  }
                  onChange={e =>
                    handleFieldChange(
                      e.value,
                      lettersNumbersAndSpecialsRegex,
                      field.onChange,
                    )
                  }
                />
              )}
            />
          </div>

          <div className="set-user-names-dialog__actions-block">
            <Button disabled={isLoading} themeColor="dark" onClick={onDismiss}>
              {t('cancel')}
            </Button>

            <Button disabled={isLoading} themeColor="primary" type="submit">
              {t('save')}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
